import { lazy, Suspense, useState } from "react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApi from "../../../shared/hooks/useApi";
import { PdfFormTemplateId } from "../../../shared/schema/schema";
import UploadPdfTemplateModal from "./UploadPdfTemplateModal";

// Lazy so pdfjs-dist/react-pdf/react-rnd end up in their own async chunk
// instead of the main bundle (the main chunk grew past the Amplify build
// machine's memory limit when these were bundled statically).
const PdfTemplateEditor = lazy(() => import("./PdfTemplateEditor"));

export default function PdfEditorSection() {
  const { api, queries } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isUploadOpen, setUploadOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<PdfFormTemplateId | null>(null);

  const listQuery = useQuery(queries.pdfFormTemplate.list());

  // Plain pattern (per OfficeBranchSettings): the group mutation binds the
  // template id via closure at creation time, which doesn't fit a per-row
  // delete button sharing one mutation instance. Call api.delete directly
  // instead, with the id passed as the mutate() argument.
  const removeMutation = useMutation({
    mutationFn: (templateId: PdfFormTemplateId) =>
      api.delete("./pdf_form_templates/:pdfFormTemplateId", {
        path: { pdfFormTemplateId: templateId },
      }),
    onError: (error) => {
      toast({ description: `Delete failed: ${error.message}`, status: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pdf-form-templates"] });
    },
  });

  return (
    <Box p={4}>
      <Flex align="center" justify="space-between" mb={4}>
        <Heading size="md">PDF Editor</Heading>
        <Button
          colorScheme="blue"
          leftIcon={<AddIcon />}
          size="sm"
          onClick={() => setUploadOpen(true)}
        >
          Upload PDF
        </Button>
      </Flex>

      {(() => {
        switch (listQuery.status) {
          case "error":
            return (
              <Alert status="error">
                <AlertIcon />
                Failed to load templates
              </Alert>
            );
          case "pending":
            return <Spinner />;
          case "success":
            return (
              <TableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Pages</Th>
                      <Th>Version</Th>
                      <Th>Status</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {listQuery.data.map((template) => (
                      <Tr
                        key={template.id}
                        _hover={{ bg: "gray.50" }}
                        cursor="pointer"
                        onClick={() => setEditingTemplateId(template.id)}
                      >
                        <Td>{template.name}</Td>
                        <Td>{template.pageCount}</Td>
                        <Td>v{template.latestVersionNumber}</Td>
                        <Td>
                          <Badge colorScheme={template.isPublished ? "green" : "yellow"}>
                            {template.isPublished ? "Published" : "Draft"}
                          </Badge>
                        </Td>
                        <Td>
                          <IconButton
                            aria-label="Delete template"
                            icon={<DeleteIcon />}
                            isLoading={removeMutation.isPending}
                            size="xs"
                            variant="ghost"
                            onClick={(event) => {
                              event.stopPropagation();
                              if (window.confirm(`Delete "${template.name}"?`)) {
                                removeMutation.mutate(template.id);
                              }
                            }}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            );
        }
      })()}

      <UploadPdfTemplateModal
        isOpen={isUploadOpen}
        onClose={() => setUploadOpen(false)}
        onCreated={(templateId) => {
          setUploadOpen(false);
          setEditingTemplateId(templateId);
        }}
      />

      {editingTemplateId !== null && (
        <Suspense fallback={<Spinner />}>
          <PdfTemplateEditor
            templateId={editingTemplateId}
            onClose={() => setEditingTemplateId(null)}
          />
        </Suspense>
      )}
    </Box>
  );
}
